import {
  ArrowDownIcon,
  ArrowUpIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  CreditCardIcon,
  FunnelIcon,
  GiftIcon,
  InformationCircleIcon,
  TableCellsIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";
import { Loader, OfficeHeader } from "components";
import React, { useMemo, useState } from "react";

import YearPicker from "layouts/calendar-wrapper/year-picker";
import clsx from "clsx";
import config from "config";
import { getBadgeColor } from "lib/utils";
import numeral from "numeral";
import { useAppSearch } from "_graphql/cache/auth";
import { useLeaderboardStatistics } from "hooks/data/use-leaderboard";

// Interfaces and Types remain the same
interface Company {
  id: string;
  name: string;
  abbreviation: string;
  points: number;
  redeemablePoints: number;
  cashEquivalent: number;
  badge: string;
  rank: number;
  colorIndex: number;
}

interface BadgeInfo {
  name: string;
  count: number;
  cashEquivalent: number;
  minimumCashEquivalent: number;
  giftCount: number;
}

type SortField = "rank" | "name" | "points" | "badge" | "cashEquivalent";
type ViewMode = "barChart" | "table";

// Constants remain the same
const COMPANY_COLORS = [
  "bg-blue-500",
  "bg-red-500",
  "bg-green-500",
  "bg-yellow-500",
  "bg-purple-500",
  "bg-pink-500",
  "bg-indigo-500",
  "bg-teal-500",
  "bg-orange-500",
  "bg-cyan-500",
  "bg-lime-500",
  "bg-emerald-500",
  "bg-violet-500",
  "bg-fuchsia-500",
  "bg-rose-500",
];

const getCompanyColor = (index: number): string => {
  return COMPANY_COLORS[index % COMPANY_COLORS.length];
};

// Hardcoded badge types for consistent ordering and display even if API doesn't return all
const ALL_BADGE_TYPES = [
  "Rookie",
  "Master",
  "Elite",
  "Captain",
  "Veteran",
  "Boss",
  "Legendary",
];

const TargetMonitorPage: React.FC = () => {
  // State hooks remain the same
  const searchParams = useAppSearch();
  const [viewMode, setViewMode] = useState<ViewMode>("barChart");
  const [sortField, setSortField] = useState<SortField>("rank");
  const [sortAscending, setSortAscending] = useState<boolean>(true);
  const [selectedBadge, setSelectedBadge] = useState<string>("all");

  // Data fetching hook remains the same
  const { leaderboard, loading, refetch, totalCashAccumulated } =
    useLeaderboardStatistics({
      brokerId: config.brokerId,
      year: searchParams?.year || new Date().getFullYear(),
    });

  // Utility functions remain the same
  const getAbbreviation = (name: string): string => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .slice(0, 3)
      .toUpperCase();
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      // Use a more specific format if desired, e.g., 'en-GB'
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Memoized data processing remains largely the same, ensure defaults
  const processedData = useMemo<Company[]>(() => {
    if (!leaderboard?.badges) return [];

    const allInsurers: Company[] = [];

    leaderboard.badges.forEach((badgeGroup) => {
      // Ensure badgeGroup and insurers exist
      if (!badgeGroup?.insurers) return;

      const badge: string = badgeGroup.name || "Unknown";

      badgeGroup.insurers.forEach((insurer) => {
        // Ensure insurer exists
        if (!insurer) return;
        const insurerName = insurer.insurer_name || "Unknown Insurer";
        allInsurers.push({
          id: insurer.id,
          name: insurerName,
          abbreviation: getAbbreviation(insurerName),
          points: insurer.total_points || 0,
          redeemablePoints: insurer.total_redeemable_points || 0,
          cashEquivalent: insurer.total_redeemable_cash_equivalent || 0,
          badge,
          rank: 0, // Rank calculated later
          colorIndex: 0, // Color index calculated later
        });
      });
    });

    // Sort by cash equivalent descending to determine initial ranking
    allInsurers.sort((a, b) => b.cashEquivalent - a.cashEquivalent);

    // Assign rank and color index based on the sorted order
    return allInsurers.map((insurer, index) => ({
      ...insurer,
      rank: index + 1,
      colorIndex: index,
    }));
  }, [leaderboard]);

  // Memoized badge types derived from processed data for consistency
  const badgeTypes = useMemo<string[]>(() => {
    const badges = new Set(processedData.map((company) => company.badge));
    // Ensure consistent order based on ALL_BADGE_TYPES
    return ALL_BADGE_TYPES.filter((badge) => badges.has(badge));
  }, [processedData]);

  // Memoized badge details remain the same
  const badgeDetails = useMemo<BadgeInfo[]>(() => {
    if (!leaderboard?.target?.target_badges) return [];

    return leaderboard.target.target_badges
      .filter((badge): badge is NonNullable<typeof badge> => badge != null)
      .map((badge) => ({
        name: badge.name || "Unknown",
        count:
          leaderboard.badges?.find((b) => b?.name === badge.name)?.count || 0, // Added optional chaining
        cashEquivalent: badge.cash_equivalent || 0,
        minimumCashEquivalent: badge.minimum_cash_equivalent || 0,
        giftCount:
          badge.gift_basket?.filter((gift) => gift != null).length || 0, // Added optional chaining
      }));
  }, [leaderboard]);

  // Memoized filtered and sorted companies remain the same
  const filteredCompanies = useMemo<Company[]>(() => {
    let companies = [...processedData];

    if (selectedBadge !== "all") {
      companies = companies.filter(
        (company) => company.badge === selectedBadge
      );
    }

    // Sorting logic remains the same
    companies.sort((a, b) => {
      const field = sortField;
      const order = sortAscending ? 1 : -1;

      switch (field) {
        case "rank":
          return (a.rank - b.rank) * order;
        case "name":
          return a.name.localeCompare(b.name) * order;
        case "badge":
          return a.badge.localeCompare(b.badge) * order;
        case "cashEquivalent":
          return (a.cashEquivalent - b.cashEquivalent) * order;
        case "points":
          return (a.points - b.points) * order;
        default:
          return 0;
      }
    });

    return companies;
  }, [processedData, sortField, sortAscending, selectedBadge]);

  // Event handlers remain the same
  const handleSort = (field: SortField): void => {
    if (sortField === field) {
      setSortAscending(!sortAscending);
    } else {
      setSortField(field);
      setSortAscending(true); // Default to ascending when changing field
    }
  };

  const handleBadgeChange = (badge: string): void => {
    setSelectedBadge(badge);
    // Refetch logic remains the same, consider debouncing if needed
    // refetch({
    //   brokerId: config.brokerId,
    //   badge: badge === "all" ? null : badge,
    // });
  };

  // Enhanced Empty State Component
  const renderEmptyState = (
    message: string = "No data available for the current selection."
  ): JSX.Element => (
    <div className="flex flex-col justify-center items-center h-64 text-tertiary text-center p-4">
      <InformationCircleIcon className="h-12 w-12 mb-3 text-gray-500" />
      <p className="text-lg font-medium">{message}</p>
    </div>
  );

  // Bar Chart Rendering - minor style tweaks possible
  const renderBarChart = (): JSX.Element => {
    if (filteredCompanies.length === 0) {
      return renderEmptyState("No company data to display in chart view.");
    }

    const maxCashEquivalent = Math.max(
      0.01, // Avoid division by zero or negative max
      ...filteredCompanies.map((c) => c.cashEquivalent)
    );
    const CHART_HEIGHT = 250; // Slightly taller chart
    const AXIS_WIDTH = 40; // Wider axis for labels
    const AXIS_HEIGHT = 250; // Wider axis for labels

    return (
      <div className="flex flex-col">
        {/* Y-Axis and Ticks */}
        <div className="flex mb-4">
          {" "}
          {/* Reduced bottom margin */}
          <div
            className="relative flex flex-col justify-between text-right pr-2" // Use flex for alignment
            style={{ width: `${AXIS_WIDTH}px`, height: `${CHART_HEIGHT}px` }}
          >
            {/* Y-Axis Line */}
            <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-600"></div>
            {/* Y-Axis Ticks (e.g., 5 ticks) */}
            {[1, 0.75, 0.5, 0.25, 0].map((ratio) => {
              const value = Math.round(maxCashEquivalent * ratio);
              const positionStyle = { top: `${(1 - ratio) * 100}%` }; // Position relative to parent height

              return (
                <div
                  key={`tick-${ratio}`}
                  className="absolute right-0 transform -translate-y-1/2" // Center vertically
                  style={positionStyle}
                >
                  <span className="text-xs text-tertiary mr-2">{value}</span>{" "}
                  {/* Short format */}
                  <div className="absolute right-[-4px] top-1/2 w-2 h-px bg-gray-600 transform -translate-y-1/2"></div>{" "}
                  {/* Tick mark */}
                </div>
              );
            })}
          </div>
          {/* Bars Area */}
          <div
            className="flex flex-1 items-end space-x-1 px-1" // Use space-x for gap, items-end
            style={{ height: `${CHART_HEIGHT}px` }}
          >
            {filteredCompanies.map((company) => {
              const barHeightPercent =
                Math.max(
                  (company.cashEquivalent / maxCashEquivalent) * 100,
                  1 // Minimum 1% height for visibility
                ) / 100;

              return (
                <div
                  key={company.id}
                  className="flex flex-col items-center flex-grow group relative" // Use flex-grow
                  title={`${company.name}: ${leaderboard?.target?.base_currency || ""} ${numeral(company.cashEquivalent).format("0,0.00")}`} // Tooltip on hover
                >
                  <div
                    className={clsx(
                      `${getCompanyColor(
                        company.colorIndex
                      )} rounded-t-sm w-full transition-all duration-300 ease-out hover:opacity-80`
                    )}
                    style={{
                      height: `${barHeightPercent * AXIS_HEIGHT}px`,
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* X-Axis Labels */}
        <div
          className="flex pr-2 space-x-1" // Align with bars, removed invalid className padding
          style={{ paddingLeft: `${AXIS_WIDTH}px` }} // Use style for dynamic padding
        >
          {filteredCompanies.map((company) => (
            <div
              key={`label-${company.id}`}
              className="flex flex-col items-center flex-grow pt-1" // Use flex-grow
              style={{ minWidth: "40px" }} // Ensure minimum width for labels
            >
              {/* Rotated Label */}
              <div className="h-16 flex items-end justify-center">
                {" "}
                {/* Container for rotation */}
                <div
                  className="transform origin-top-left text-xs text-tertiary truncate whitespace-nowrap"
                  style={{ width: "60px" }} // Adjust width as needed
                  title={company.name} // Full name on hover
                >
                  {company.abbreviation || company.name}
                </div>
              </div>
              <div className="text-xs text-tertiary truncate whitespace-nowrap">
                GHS {numeral(company.cashEquivalent).format("0,0.00")}
              </div>
              {/* Rank below label */}
              <div
                className={clsx(
                  company.rank <= 3 ? "text-teal-500" : "text-tertiary",
                  "mt-1 text-sm font-extrabold"
                )}
              >
                #{company.rank}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Table View Rendering - Standardized icons, hover effect
  const renderTableView = (): JSX.Element => {
    if (filteredCompanies.length === 0) {
      return renderEmptyState("No company data to display in table view.");
    }

    const SortIcon = ({ field }: { field: SortField }) => {
      if (sortField !== field) return null;
      return sortAscending ? (
        <ArrowUpIcon className="h-4 w-4 ml-1 text-tertiary" />
      ) : (
        <ArrowDownIcon className="h-4 w-4 ml-1 text-tertiary" />
      );
    };

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-left min-w-[600px]">
          {" "}
          {/* Added min-width */}
          <thead>
            <tr className="border-b border-gray-700">
              {/* Table Headers with consistent styling */}
              {(
                [
                  "rank",
                  "name",
                  "cashEquivalent",
                  "badge",
                  "points",
                ] as SortField[]
              ).map((field) => (
                <th
                  key={field}
                  className="pb-3 px-3 cursor-pointer group" // Added padding and group
                  onClick={() => handleSort(field)}
                >
                  <div className="flex items-center">
                    <span className="text-sm font-semibold text-tertiary uppercase tracking-wider group-hover:text-white transition-colors">
                      {" "}
                      {/* Enhanced text style */}
                      {field === "cashEquivalent"
                        ? "Cash Equiv."
                        : field.charAt(0).toUpperCase() + field.slice(1)}
                    </span>
                    <SortIcon field={field} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {" "}
            {/* Added divider */}
            {filteredCompanies.map((company) => (
              <tr
                key={company.id}
                className="hover:bg-gray-700/50 transition-colors duration-150"
              >
                {" "}
                {/* Hover effect */}
                {/* Rank Cell */}
                <td className="py-3 px-3">
                  <div className="flex items-center justify-start">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium ${
                        company.rank <= 3
                          ? "bg-primary-500/20 text-primary" // Use primary color theme
                          : "bg-gray-600 text-tertiary"
                      }`}
                    >
                      {company.rank}
                    </div>
                  </div>
                </td>
                {/* Company Name Cell */}
                <td className="py-3 px-3">
                  <div className="flex items-center">
                    <div
                      className={`flex-shrink-0 rounded-full ${getCompanyColor(
                        company.colorIndex
                      )} p-1.5 mr-3`} // Adjusted padding
                    >
                      <BuildingOfficeIcon className="h-4 w-4 text-white" />{" "}
                      {/* Slightly smaller icon */}
                    </div>
                    <span className="text-primary font-medium text-sm whitespace-nowrap">
                      {" "}
                      {/* Added whitespace-nowrap */}
                      {company.name}
                    </span>
                  </div>
                </td>
                {/* Cash Equivalent Cell */}
                <td className="py-3 px-3 text-primary text-sm whitespace-nowrap">
                  {leaderboard?.target?.base_currency || "CUR"}{" "}
                  {/* Default currency */}
                  {numeral(company.cashEquivalent).format("0,0.00")}
                </td>
                {/* Badge Cell */}
                <td className="py-3 px-3">
                  <div className="flex items-center">
                    <div
                      className={`w-2.5 h-2.5 rounded-sm ${getBadgeColor(
                        company.badge
                      )} mr-2 flex-shrink-0`} // Smaller indicator
                    ></div>
                    <span className="text-primary text-sm">
                      {company.badge}
                    </span>
                  </div>
                </td>
                {/* Points Cell */}
                <td className="py-3 px-3 text-primary text-sm whitespace-nowrap">
                  {numeral(company.points).format("0,0")} pts
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Content Renderer remains the same
  const renderContent = (): JSX.Element => {
    if (loading)
      return (
        <div className="flex justify-center items-center h-64">
          <Loader />
        </div>
      ); // Centered loader
    return viewMode === "barChart" ? renderBarChart() : renderTableView();
  };

  // Badge Distribution Rendering - Use ALL_BADGE_TYPES for consistency
  const renderBadgeDistribution = (): JSX.Element => {
    const totalCompanies = processedData.length; // Use processedData length for total

    // Check if badge details are available
    const hasBadgeDetails = badgeDetails.length > 0;

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {ALL_BADGE_TYPES.map((badgeName) => {
          const badgeData = leaderboard?.badges?.find(
            // Find from API data if available
            (b) => b?.name === badgeName
          );
          const count = badgeData?.count || 0;
          const percentage =
            totalCompanies > 0 ? Math.round((count / totalCompanies) * 100) : 0;

          const badgeDetail = badgeDetails.find((b) => b.name === badgeName);
          const cashEquivalent = badgeDetail?.cashEquivalent || 0;
          const minCashEquivalent = badgeDetail?.minimumCashEquivalent || 0;
          const giftCount = badgeDetail?.giftCount || 0;

          // Only render if the badge exists in the data or has details
          // Or always render all types, showing 0 if no data
          // if (count === 0 && !badgeDetail) return null; // Option: Hide empty badges

          return (
            <div
              key={badgeName}
              className="bg-card-700 rounded-lg p-4 shadow-md flex flex-col"
            >
              {" "}
              {/* Use card color, shadow */}
              {/* Badge Header */}
              <div className="flex items-center mb-2">
                <div
                  className={`flex-shrink-0 rounded-full ${getBadgeColor(
                    badgeName
                  )} p-1.5 mr-2`} // Adjusted padding
                >
                  <TrophyIcon className="h-4 w-4 text-white" />
                </div>
                <span className="text-primary font-semibold text-sm">
                  {badgeName}
                </span>
              </div>
              {/* Count and Percentage */}
              <div className="text-2xl font-bold text-primary">{count}</div>
              <div className="text-tertiary text-xs mb-3">
                {percentage}% of companies
              </div>
              {/* Badge Target Details (if available) */}
              {hasBadgeDetails && badgeDetail && (
                <div className="mt-auto pt-3 border-t border-gray-600 space-y-1">
                  {" "}
                  {/* Pushes to bottom */}
                  <div className="text-tertiary text-xs flex justify-between items-center">
                    <span>Min Target:</span>
                    <span className="text-secondary font-medium">
                      {leaderboard?.target?.base_currency || "CUR"}{" "}
                      {numeral(minCashEquivalent).format("0,0")}
                    </span>
                  </div>
                  <div className="text-tertiary text-xs flex justify-between items-center">
                    <span>Max Target:</span>
                    <span className="text-secondary font-medium">
                      {leaderboard?.target?.base_currency || "CUR"}{" "}
                      {numeral(cashEquivalent).format("0,0")}
                    </span>
                  </div>
                  <div className="text-tertiary text-xs flex items-center">
                    <GiftIcon className="h-3 w-3 mr-1 flex-shrink-0 " />
                    <span>
                      {giftCount} Gift{giftCount !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
              )}
              {/* Placeholder if no details */}
              {hasBadgeDetails && !badgeDetail && (
                <div className="mt-auto pt-3 border-t border-gray-600">
                  <p className="text-xs text-gray-500 italic">
                    Target details not available.
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Summary Cards Rendering - Minor style consistency check
  const renderSummaryCards = (): JSX.Element => {
    // Calculate totals based on filtered data
    const totalFilteredCash = filteredCompanies.reduce(
      (sum, company) => sum + company.cashEquivalent,
      0
    );
    const totalFilteredPoints = filteredCompanies.reduce(
      (sum, company) => sum + company.points,
      0
    );
    const filteredBadgeCount =
      selectedBadge !== "all"
        ? filteredCompanies.filter((c) => c.badge === selectedBadge).length
        : filteredCompanies.length; // This is same as filteredCompanies.length if 'all'

    const summaryData = [
      {
        title: "Total Companies",
        value: processedData.length, // Show total companies regardless of filter
        icon: BuildingOfficeIcon,
        color: "bg-blue-500/20", // Example color adjustment
        iconColor: "text-blue-400",
      },
      {
        title: "Total Cash Equivalent (Filtered)",
        value: totalFilteredCash,
        icon: TrophyIcon,
        color: "bg-sky-800/20", // Use primary theme color
        iconColor: "text-sky-400",
        format: `${leaderboard?.target?.base_currency || "CUR"} 0,0.00`,
      },
      {
        title: "Total Points (Filtered)",
        value: totalFilteredPoints,
        icon: ChartBarIcon, // Changed icon for variety
        color: "bg-green-500/20", // Example color adjustment
        iconColor: "text-green-400",
        format: "0,0",
      },
      {
        title: "Total Cash Accumulated",
        value: totalCashAccumulated,
        icon: CreditCardIcon, // Changed icon for variety
        color: "bg-teal-500/20", // Example color adjustment
        iconColor: "text-teal-400",
        format: "0,0",
      },
      {
        title: `${selectedBadge !== "all" ? selectedBadge : "Filtered"} Companies`,
        value: filteredCompanies.length, // Count after filtering
        icon: FunnelIcon,
        color: "bg-yellow-500/20", // Example color adjustment
        iconColor: "text-yellow-400",
      },
    ];

    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {summaryData.map((item, index) => (
          <div
            key={index}
            className="relative overflow-hidden rounded-lg bg-card-500 p-5 shadow-md" // Use card color, consistent padding
          >
            <dt>
              <div className={`absolute rounded-md ${item.color} p-2.5`}>
                {" "}
                {/* Adjusted padding */}
                <item.icon
                  className={`h-5 w-5 ${item.iconColor}`} // Use specific icon color
                  aria-hidden="true"
                />
              </div>
              <p className="ml-12 truncate text-sm font-medium text-tertiary">
                {" "}
                {/* Adjusted margin */}
                {item.title}
              </p>
            </dt>
            <dd className="ml-12 flex items-baseline pb-1">
              {" "}
              {/* Adjusted margin */}
              <p className="text-2xl font-semibold text-primary">
                {" "}
                {/* Brighter text */}
                {item.format
                  ? numeral(item.value).format(item.format)
                  : numeral(item.value).format("0,0")}
              </p>
              {/* Optional: Add comparison or trend later */}
            </dd>
          </div>
        ))}
      </div>
    );
  };

  return (
    <main className="flex-1 flex flex-col overflow-hidden h-screen ">
      <OfficeHeader renderActions={() => <YearPicker />} />
      <div className="flex-1 p-4 sm:p-6 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
        {" "}
        {/* Custom scrollbar */}
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-primary mb-2">
            Insurance Companies Leaderboard
          </h1>
          {/* Target Info */}
          <div className="text-sm text-tertiary mb-4">
            Target: {leaderboard?.target?.target_name || "Default Target"} |{" "}
            {formatDate(leaderboard?.target?.start_date)} to{" "}
            {formatDate(leaderboard?.target?.end_date)} | Currency:{" "}
            {leaderboard?.target?.base_currency || "GHS"}
          </div>

          {/* Controls: View Mode and Badge Filter */}
          <div className="flex flex-wrap gap-4 items-center justify-between">
            {/* Badge Filter Dropdown */}
            {badgeTypes.length > 0 && (
              <div className="flex items-center gap-2">
                <label
                  htmlFor="badge-filter"
                  className="text-sm font-medium text-gray-300"
                >
                  Filter by Badge:
                </label>
                <select
                  id="badge-filter"
                  value={selectedBadge}
                  onChange={(e) => handleBadgeChange(e.target.value)}
                  className="bg-card-700 text-primary rounded-md px-3 py-1.5 border border-gray-600 focus:ring-primary-500 focus:border-primary-500 text-sm transition duration-150 ease-in-out" // Enhanced styling
                >
                  <option value="all">All Badges</option>
                  {badgeTypes.map((badge) => (
                    <option key={badge} value={badge}>
                      {badge}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* View Mode Toggle */}
            <div className="flex gap-2">
              {(["barChart", "table"] as ViewMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-150 ease-in-out ${
                    viewMode === mode
                      ? "bg-card-800 text-primary shadow-sm" // Active state
                      : "bg-card-700 text-tertiary hover:bg-gray-600" // Inactive state
                  }`}
                >
                  {mode === "barChart" ? (
                    <ChartBarIcon className="h-4 w-4 mr-1.5" />
                  ) : (
                    <TableCellsIcon className="h-4 w-4 mr-1.5" />
                  )}
                  {mode === "barChart" ? "Chart" : "Table"}
                </button>
              ))}
            </div>
          </div>
        </div>
        {/* Summary Cards Section */}
        <div className="mb-6">
          {loading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="h-24 bg-card-500 rounded-lg animate-pulse"
                ></div>
              ))}
            </div>
          ) : (
            renderSummaryCards()
          )}
        </div>
        {/* Main Content Area: Performance Chart/Table */}
        <div className="bg-card-500/80 backdrop-blur-sm rounded-lg p-4 sm:p-6 mb-6 shadow-lg">
          <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
            <h2 className="text-lg font-semibold text-primary">
              Companies Performance
            </h2>
            {/* Filter Indicator */}
            <div className="flex items-center space-x-2 text-sm text-tertiary">
              <FunnelIcon className="h-4 w-4" />
              <span>
                {selectedBadge === "all"
                  ? "Showing All Badges"
                  : `Filtered by: ${selectedBadge} Badge`}
              </span>
            </div>
          </div>
          {/* Render Chart or Table */}
          {renderContent()}
        </div>
        {/* Badge Distribution Section */}
        {badgeTypes.length > 0 && (
          <div className=" backdrop-blur-sm rounded-lg p-4 sm:p-6 shadow-lg">
            <h2 className="text-lg font-semibold text-primary mb-4">
              Badge Distribution & Targets
            </h2>
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(ALL_BADGE_TYPES.length)].map((_, i) => (
                  <div
                    key={i}
                    className="h-36 bg-card-600 rounded-lg animate-pulse"
                  ></div>
                ))}
              </div>
            ) : (
              renderBadgeDistribution()
            )}
          </div>
        )}
      </div>
    </main>
  );
};

export default TargetMonitorPage;
